import { ref, computed } from 'vue'
import type { AuthResponse, AuthUser } from '@/types'

const SESSION_DURATION_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export const isDevBypassAuth = () =>
  import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

const getStoredToken = () => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('api_access_token') || sessionStorage.getItem('api_access_token')
}

const getStoredUser = (): AuthUser | null => {
  if (typeof window === 'undefined') return null
  const email = localStorage.getItem('auth_user_email')
  const name = localStorage.getItem('auth_user_name')
  if (!email) return null
  return { email, name: name || email.split('@')[0] }
}

const isTokenExpired = () => {
  if (isDevBypassAuth()) return false
  const expiresAt = localStorage.getItem('api_access_expires_at')
  if (!expiresAt) return true
  return Date.now() > parseInt(expiresAt)
}

const refreshExpiry = () => {
  localStorage.setItem('api_access_expires_at', String(Date.now() + SESSION_DURATION_MS))
}

const accessToken = ref<string | null>(getStoredToken())
const currentUser = ref<AuthUser | null>(getStoredUser())

export function useAuth() {
  const isAuthenticated = computed(() => !!accessToken.value)

  const _storeUser = (user?: { email?: string; name?: string }) => {
    if (user?.email) {
      const authUser: AuthUser = {
        email: user.email,
        name: user.name || user.email.split('@')[0]
      }
      currentUser.value = authUser
      localStorage.setItem('auth_user_email', authUser.email)
      localStorage.setItem('auth_user_name', authUser.name)
    }
  }

  const _storeToken = (token: string, user?: { email?: string; name?: string }) => {
    accessToken.value = token
    localStorage.setItem('api_access_token', token)
    sessionStorage.removeItem('api_access_token')
    refreshExpiry()
    _storeUser(user)
  }

  const applyDevBypass = (): boolean => {
    if (!isDevBypassAuth()) return false

    const token = import.meta.env.VITE_DEV_ACCESS_TOKEN
    if (!token) {
      console.warn('[dev:bypass] VITE_DEV_ACCESS_TOKEN is not set')
      return false
    }

    _storeToken(token, {
      email: import.meta.env.VITE_DEV_USER_EMAIL || 'dev@localhost',
      name: import.meta.env.VITE_DEV_USER_NAME || 'Local Dev'
    })
    return true
  }

  const authenticateWithGoogle = async (credential: string): Promise<boolean> => {
    const response = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Google authentication failed')
    }

    const data: AuthResponse = await response.json()
    _storeToken(data.token, { email: data.email, name: data.name })
    return true
  }

  const authenticate = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Authentication failed')
      }

      const data: AuthResponse = await response.json()
      _storeToken(data.token)
      return true
    } catch (error) {
      console.error('Auth error:', error)
      throw error
    }
  }

  const logout = () => {
    accessToken.value = null
    currentUser.value = null
    localStorage.removeItem('api_access_token')
    localStorage.removeItem('api_access_expires_at')
    localStorage.removeItem('auth_user_email')
    localStorage.removeItem('auth_user_name')
    sessionStorage.removeItem('api_access_token')
  }

  /** True when bearer token exists but Google profile was never stored (pre–creator-tracking sessions). */
  const needsProfileRefresh = () =>
    !isDevBypassAuth() && !!accessToken.value && !currentUser.value?.email

  const checkAuth = async (): Promise<boolean> => {
    if (isDevBypassAuth()) {
      applyDevBypass()
      return true
    }

    if (!accessToken.value) return false

    if (needsProfileRefresh()) {
      logout()
      return false
    }

    if (isTokenExpired()) {
      logout()
      return false
    }

    try {
      const response = await fetch('/api/links', {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`
        }
      })

      if (response.status === 401) {
        logout()
        return false
      }

      // Refresh the 30-day window on every successful visit
      refreshExpiry()
      return true
    } catch (error) {
      console.error('Auth check error:', error)
      logout()
      return false
    }
  }

  const getToken = () => accessToken.value
  const getUser = () => currentUser.value

  return {
    isAuthenticated,
    currentUser,
    isDevBypassAuth,
    authenticate,
    authenticateWithGoogle,
    applyDevBypass,
    logout,
    checkAuth,
    getToken,
    getUser,
    needsProfileRefresh
  }
}

if (typeof window !== 'undefined' && isDevBypassAuth()) {
  const { applyDevBypass } = useAuth()
  applyDevBypass()
  console.info('[dev:bypass] Local auth bypass enabled — no Google login required')
}
